import React, { useMemo } from 'react';
import { usePrintSettings } from '../../context/PrintSettingsContext';
import { challanPrintOverrideCss } from '../../utils/challanPrintLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { escapeHtml } from '../../utils/escapeHtml';

const formatCurrency = (v) => {
  if (v === null || typeof v === 'undefined' || v === '') return '₹ 0.00';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('en-IN', { 
    style: 'currency', 
    currency: 'INR', 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

const formatNumber = (v) => {
  if (v === null || typeof v === 'undefined' || v === '') return '0';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('en-IN');
};

const formatDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return d;
  }
};

const PurchaseChallanPrintModal = ({ isOpen, onClose, data, loading }) => {
    const { purchaseChallanPaperSize: challanPaperSize } = usePrintSettings();
  const printableHtml = useMemo(() => {
    if (!data) return '';
    const safe = (v) => (v === null || typeof v === 'undefined' || v === '' ? '-' : escapeHtml(v)); // print window (P1-2)
    const num = (v) => parseFloat(v) || 0;
    
    // Map items from JSON
    const items = Array.isArray(data.items) ? data.items.filter(it => it && Object.keys(it).length > 0) : [];
    
    // Calculate totals from items for accuracy
    let baseItemTotal = 0;
    let totalGst = 0;
    
    items.forEach(it => {
        const itemQty = num(it.quantity);
        const itemPrice = num(it.price);
        baseItemTotal += (itemQty * itemPrice);
        totalGst += num(it.gst_amount);
    });

    const expenses = num(data.expenses || 0);
    const expenseGst = num(data.expense_gst_amount || 0);
    const discount = num(data.discount || 0);
    const discountGst = num(data.discount_gst_amount || 0);
    
    const intermediateSubtotal = baseItemTotal + expenses - discount;
    const finalGst = totalGst + expenseGst - discountGst;
    const roundingOff = num(data.rounding_off || 0);
    const grandTotal = intermediateSubtotal + finalGst + roundingOff;

    // HSN Summary Calculation
    const hsnSummaryMap = items.reduce((acc, it) => {
      const hsn = safe(it.hsn_code) || 'N/A';
      if (!acc[hsn]) {
        acc[hsn] = { qty: 0, taxable: 0, gst_amount: 0, gst: num(it.gst_percentage), unit: safe(it.unit) };
      }
      acc[hsn].qty += num(it.quantity);
      acc[hsn].taxable += (num(it.quantity) * num(it.price));
      acc[hsn].gst_amount += num(it.gst_amount);
      return acc;
    }, {});

    const itemsRows = items.map((it, idx) => {
        const adviceInfo = Array.isArray(it.advices) ? it.advices.map(adv => `
            <div style="font-size: 6px; color: #666; margin-top: 1px;">
              Adv: ${safe(adv.advice_number)} | Veh: ${safe(adv.vehicle_number)} | Recv: ${formatNumber(adv.actual_received)} ${safe(it.unit)}
            </div>
        `).join('') : '';

        return `
      <tr>
        <td style="text-align: center;">${idx + 1}</td>
        <td>
          <div style="font-weight: 700; font-size: 8px;">${safe(it.product_name)}</div>
          <div style="font-size: 7px; color: #444;">${safe(it.size_name)}</div>
          ${adviceInfo}
        </td>
        <td style="text-align: center;">${it.actual_pieces || it.quantity_in_pieces || '-'}</td>
        <td style="text-align: right;">${formatNumber(it.quantity)} ${it.unit}</td>
        <td style="text-align: center;">${it.gst_percentage}%</td>
        <td style="text-align: right;">${formatCurrency(it.price).replace('₹', '')}</td>
        <td style="text-align: right;">${formatCurrency(it.amount).replace('₹', '')}</td>
      </tr>`;
    }).join('\n');

    const hsnRows = Object.entries(hsnSummaryMap).map(([hsn, hData]) => `
      <tr>
        <td>${hsn}</td>
        <td style="text-align: right;">${formatNumber(hData.qty)} ${hData.unit}</td>
        <td style="text-align: right; font-weight: bold;">${formatCurrency(hData.taxable + hData.gst_amount).replace('₹', '')}</td>
      </tr>`).join('');

    return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Purchase Challan - ${safe(data.po_number)}</title>
  <style>
    @page { size: A5 landscape; margin: 3mm; }
    * { box-sizing: border-box; font-family: sans-serif; color: #000; }
    body { margin: 0; padding: 0; font-size: 8.5px; line-height: 1.1; }
    .no-print { text-align: center; padding: 10px; background: #f5f5f5; border-bottom: 1px solid #ddd; }
    .print-container { width: 100%; max-width: 148mm; margin: 0 auto; color: #000; }
    
    .title { text-align: center; font-size: 11px; font-weight: bold; border: 1px solid #000; padding: 2px 0; background: #eee; border-top-left-radius: 2px; border-top-right-radius: 2px; }
    
    .header-table { width: 100%; border: 1px solid #000; border-top: 0; margin-bottom: 4px; border-collapse: collapse; }
    .header-table td { padding: 3px; vertical-align: top; border: 0.5px solid #ccc; }
    
    table.data-table { width: 100%; border-collapse: collapse; margin-bottom: 6px; border: 1px solid #000; }
    table.data-table th, table.data-table td { border: 1px solid #000; padding: 2px 4px; }
    table.data-table th { background: #eee; font-weight: bold; text-transform: uppercase; font-size: 7px; }
    
    .footer-section { display: flex; justify-content: space-between; gap: 8px; align-items: flex-start; margin-top: 4px; }
    
    .hsn-summary-title { font-size: 7px; font-weight: bold; text-transform: uppercase; margin-bottom: 1px; color: #444; }
    table.hsn-table { width: 100%; border-collapse: collapse; border: 0.5px solid #ccc; }
    table.hsn-table th, table.hsn-table td { border: 0.5px solid #ccc; padding: 1.5px 4px; font-size: 7px; }
    table.hsn-table th { background: #fafafa; font-weight: bold; font-size: 6px; }

    .summary-table { width: 220px; border-collapse: collapse; border: 1px solid #000; }
    .summary-table td { padding: 2px 4px; border: 0.5px solid #eee; font-size: 8px; }
    .grand-total { border-top: 2.5px double #000 !important; font-weight: 900; font-size: 10.5px; background: #eee; }
    .subtotal-row { border-top: 1px solid #000; border-bottom: 1px solid #000; font-weight: 700; background: #fafafa; }
    
    @media print { .no-print { display: none !important; } }
  ${challanPrintOverrideCss(challanPaperSize, 'landscape')}
  </style>
</head>
<body>
  <div class="print-container">
    <div class="title">PURCHASE CHALLAN</div>
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; border: 1px solid #000; border-top: 0; padding: 4px; margin-bottom: 4px;">
      <div style="font-size: 7px;">PO: ${safe(data.po_number)} (${formatDate(data.date)})</div>
      <div style="font-size: 7px; text-align: right;">WT: ${formatNumber(data.total_weight)} KG</div>
    </div>

    <table class="header-table">
      <tr>
        <td style="width: 63%;">
          <div style="font-size: 7px; color: #666; font-weight: bold;">SUPPLIER:</div>
          <div style="font-weight: bold; font-size: 9px;">${safe(data.supplier_name)}</div>
          <div style="margin-top: 4px;">PO NUMBER: ${safe(data.po_number)}</div>
          <div style="margin-top: 2px;">LOCATION: ${safe(data.delivery_location_name)}</div>
        </td>
        <td>
          <div style="font-size: 7px; color: #666; font-weight: bold;">ORDER DATE:</div>
          <div style="font-weight: bold;">${formatDate(data.date)}</div>
          <div style="margin-top: 4px;">CONTACT: ${safe(data.contact_person)}</div>
          <div style="margin-top: 2px; font-size: 7px;">WT: ${formatNumber(data.total_weight)} KG</div>
        </td>
      </tr>
    </table>

    <table class="data-table">
      <thead>
        <tr>
          <th style="width: 20px;">Sr.</th>
          <th>Item Description</th>
          <th style="width: 30px;">Pcs</th>
          <th style="width: 60px;">Ord Qty</th>
          <th style="width: 30px;">GST</th>
          <th style="width: 60px;">Rate (₹)</th>
          <th style="width: 70px;">Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows}
      </tbody>
    </table>

    <div class="footer-section">
      <div style="width: 58%;">
        <div class="hsn-summary-title">HSN SUMMARY</div>
        <table class="hsn-table">
          <thead>
            <tr>
              <th>HSN Code</th>
              <th style="text-align: right;">Total Qty</th>
              <th style="text-align: right;">Total Val (Inc. GST)</th>
            </tr>
          </thead>
          <tbody>
            ${hsnRows}
          </tbody>
        </table>
        
        <div style="margin-top: 6px;">
          <div style="margin-top: 3px; font-size: 7px;">Shipping To: ${safe(data.shipping_destination_name)} | Tot Qty: ${formatNumber(data.total_quantity)}</div>
          ${data.remark || data.po_remark ? `<div style="margin-top: 4px; font-size: 7px; font-weight: bold; border: 0.5px dashed #ccc; padding: 2px;">REMARK: ${safe(data.remark || data.po_remark)}</div>` : ''}
        </div>
      </div>
      
      <div style="width: 40%; display: flex; justify-content: flex-end;">
        <table class="summary-table">
          <tr><td>Item Total</td><td style="text-align: right;">${formatCurrency(baseItemTotal)}</td></tr>
          ${expenses > 0 ? `<tr><td>Expense</td><td style="text-align: right;">${formatCurrency(expenses)}</td></tr>` : ''}
          ${discount > 0 ? `<tr><td>Discount</td><td style="text-align: right; color: #d00;">- ${formatCurrency(discount)}</td></tr>` : ''}
          <tr class="subtotal-row"><td>Subtotal</td><td style="text-align: right;">${formatCurrency(intermediateSubtotal)}</td></tr>
          <tr><td>+GST</td><td style="text-align: right;">${formatCurrency(finalGst)}</td></tr>
          ${roundingOff !== 0 ? `<tr><td style="color:#6b7280;">Rounding Off</td><td style="text-align: right; color:#6b7280;">${formatCurrency(roundingOff)}</td></tr>` : ''}
          <tr class="grand-total"><td>Grand Total</td><td style="text-align: right;">${formatCurrency(grandTotal)}</td></tr>
        </table>
      </div>
    </div>
  </div>
</body>
</html>`;
  }, [data, challanPaperSize]);

  if (!isOpen) return null;

  // UI Totals Mapping
  const items = Array.isArray(data?.items) ? data.items : [];
  let baseItemTotal = 0;
  let totalGst = 0;
  items.forEach(it => {
      baseItemTotal += (Number(it.price || 0) * Number(it.quantity || 0));
      totalGst += Number(it.gst_amount || 0);
  });

  const expenses = Number(data?.expenses || 0);
  const discount = Number(data?.discount || 0);
  const intermediateSubtotal = baseItemTotal + expenses - discount;
  const finalGst = totalGst + (Number(data?.expense_gst_amount || 0)) - (Number(data?.discount_gst_amount || 0));
  const roundingOff = Number(data?.rounding_off || 0);
  const grandTotal = intermediateSubtotal + finalGst + roundingOff;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl flex flex-col overflow-hidden max-h-[90vh]">
        {/* Simple Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gray-50">
          <h2 className="text-lg font-bold">Purchase Order - {data?.po_number || 'PO-XXXX'}</h2>
          <div className="flex gap-2">
            <button onClick={() => {
              const w = window.open('', '_blank');
              if (!w) {
                // Pop-up blocked — w is null; w.document.* would throw an uncaught
                // TypeError and the print would die silently. Tell the user instead.
                alert('Please allow pop-ups for this site to print.');
                return;
              }
              w.document.open();
              w.document.write(printableHtml);
              w.document.close();
              setTimeout(() => { w.focus(); w.print(); }, 300);
            }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              <FontAwesomeIcon icon={faPrint} /> Print
            </button>
            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Plain Body */}
        <div className="p-6 overflow-y-auto space-y-6">
          {loading ? (
            <div className="py-12 text-center text-gray-500"><FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> Loading...</div>
          ) : data ? (
            <>

              {/* Basic Details Table */}
              <table className="w-full text-sm border-collapse">
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-bold text-gray-500 w-1/4">Supplier</td>
                    <td className="py-2 font-medium">{data.supplier_name}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-bold text-gray-500">PO Number</td>
                    <td className="py-2">{data.po_number} ({formatDate(data.date)})</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-bold text-gray-500">Destination</td>
                    <td className="py-2">{data.shipping_destination_name} | {data.delivery_location_name}</td>
                  </tr>
                   <tr className="border-b">
                    <td className="py-2 font-bold text-gray-500">Contact</td>
                    <td className="py-2">{data.contact_person} ({data.mobile_number})</td>
                  </tr>
                  {(data.remark || data.po_remark) && (
                    <tr className="border-b">
                      <td className="py-2 font-bold text-gray-500">Order Remark</td>
                      <td className="py-2">
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                          {data.remark || data.po_remark}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Items Table */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left border-b border-t">
                    <th className="py-2 px-1">Item Details</th>
                    <th className="py-2 px-1 text-center">Pcs</th>
                    <th className="py-2 px-1 text-right">Ord Qty</th>
                    <th className="py-2 px-1 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.items?.map((it, idx) => (
                    <tr key={idx}>
                      <td className="py-2 px-1">
                        <div className="font-medium text-gray-900">{it.product_name}</div>
                        <div className="text-xs text-gray-400">{it.size_name}</div>
                        {Array.isArray(it.advices) && it.advices.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {it.advices.map((adv, aIdx) => (
                              <div key={aIdx} className="text-[10px] text-blue-600 bg-blue-50/50 px-1.5 py-0.5 rounded-sm inline-block mr-1">
                                {adv.advice_number} | {formatNumber(adv.actual_received)} {it.unit}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-1 text-center">{it.actual_pieces || it.quantity_in_pieces}</td>
                      <td className="py-2 px-1 text-right">{formatNumber(it.quantity)} {it.unit}</td>
                      <td className="py-2 px-1 text-right font-medium">{formatCurrency(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals Section */}
              <div className="flex justify-end">
                <div className="w-72 space-y-1.5 text-sm border-t pt-4">
                  <div className="flex justify-between"><span>Item Total</span><span>{formatCurrency(baseItemTotal)}</span></div>
                  {expenses > 0 && <div className="flex justify-between"><span>Expense</span><span>{formatCurrency(expenses)}</span></div>}
                  {discount > 0 && <div className="flex justify-between text-red-600"><span>Discount</span><span>- {formatCurrency(discount)}</span></div>}
                  <div className="flex justify-between font-bold bg-gray-50 px-2 py-1"><span>Subtotal</span><span>{formatCurrency(intermediateSubtotal)}</span></div>
                  <div className="flex justify-between"><span>+GST</span><span>{formatCurrency(finalGst)}</span></div>
                  {roundingOff !== 0 && <div className="flex justify-between text-gray-500"><span>Rounding Off</span><span>{formatCurrency(roundingOff)}</span></div>}
                  <div className="flex justify-between font-black text-xl border-t-2 border-gray-900 pt-2 mt-2">
                    <span>Grand Total</span>
                    <span>{formatCurrency(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-gray-500">No data found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseChallanPrintModal;
